// eslint-disable-next-line @typescript-eslint/no-unused-vars -- grace - before eslint enforced
import { emptyHours } from "../../defaults/basic";
import { IAgentHours, ITimeSlot } from "../../types/hours";

function isSlotSplitNeeded(slot: ITimeSlot): boolean {
  if (!slot.open || !slot.close) return false;
  const [hour1, min1] = slot.open.split(":").map(Number);
  const [hour2, min2] = slot.close.split(":").map(Number);
  return hour2 < hour1 || (hour2 === hour1 && min2 < min1);
}

function mergeOverlappingSlots(schedule: IAgentHours): IAgentHours {
  Object.entries(schedule).forEach(([day, slots]) => {
    schedule[day as keyof IAgentHours] = mergeSlots(slots);
  });
  return schedule;
}

function mergeSlots(slots: ITimeSlot[]): ITimeSlot[] {
  if (slots.length <= 1) return slots;

  // Sort slots by opening time
  const sortedSlots = [...slots].sort((a, b) => {
    if (!a.open || !b.open) return 0;
    return a.open.localeCompare(b.open);
  });

  const merged: ITimeSlot[] = [];
  let current = sortedSlots[0];

  for (let i = 1; i < sortedSlots.length; i++) {
    const next = sortedSlots[i];

    if (!current.open || !current.close || !next.open || !next.close) continue;

    const currentClose = timeToMinutes(current.close);
    const nextOpen = timeToMinutes(next.open);
    const nextClose = timeToMinutes(next.close);

    // If slots overlap or are adjacent
    if (currentClose >= nextOpen) {
      // Merge by taking the later closing time
      current = {
        ...current,
        close: minutesToTime(Math.max(currentClose, nextClose)),
      };
    } else {
      merged.push(current);
      current = next;
    }
  }

  merged.push(current);
  return merged;
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
}

function is24HourDay(slots: ITimeSlot[] | undefined): boolean {
  if (!slots || slots.length !== 1) return false;
  const slot = slots[0];
  return slot.open === "00:00" && slot.close === "23:59";
}

const daysOfTheWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
export function convertHoursToBackend(hours: IAgentHours | null) {
  if (!hours) return null;

  const newSchedule: IAgentHours = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  for (const [index, day] of daysOfTheWeek.entries()) {
    const slots = hours[day as keyof IAgentHours];
    if (!Array.isArray(slots)) continue;
    for (const slot of slots) {
      if (slot.open && slot.close) {
        // Handle 24-hour slots (same open and close time)
        if (slot.open === slot.close) {
          newSchedule[day as keyof IAgentHours].push({
            open: "00:00",
            close: "23:59",
          });
          continue;
        }

        // if there's any slots where the start is before midnight and the end is after midnight,
        // we need to split them into two days
        if (isSlotSplitNeeded(slot as ITimeSlot)) {
          const firstSlot = {
            open: slot.open,
            close: "23:59",
          };
          const secondSlot = {
            open: "00:00",
            close: slot.close,
          };
          newSchedule[day as keyof IAgentHours].push(firstSlot);
          const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
          newSchedule[nextDay as keyof IAgentHours].push(secondSlot);
        } else {
          newSchedule[day as keyof IAgentHours].push({
            open: slot.open,
            close: slot.close,
          });
        }
      }
    }
  }

  return mergeOverlappingSlots(newSchedule);
}

export function isHoursEmpty(hours: IAgentHours): boolean {
  return Object.values(hours).every((slots: ITimeSlot[]) => slots.length === 0);
}

export function convertHoursToFrontend(
  schedule: IAgentHours | null,
): IAgentHours | null {
  if (!schedule) return null;
  const frontendSchedule: IAgentHours = JSON.parse(JSON.stringify(schedule));
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  for (let i = 0; i < days.length; i++) {
    const currentDayName = days[i];
    const currentDaySlots =
      frontendSchedule[currentDayName as keyof IAgentHours];
    if (!currentDaySlots || currentDaySlots.length === 0) {
      continue; // no slots for this day
    }
    const lastSlotOfTheDay = currentDaySlots[currentDaySlots.length - 1];
    const isCurrentDay24Hours = is24HourDay(currentDaySlots);

    // try to merge it into the next day if next day is not 24 hour day
    if (lastSlotOfTheDay.close === "23:59" && !isCurrentDay24Hours) {
      const nextDayName = days[(i + 1) % 7];
      const nextDaySlots = frontendSchedule[nextDayName as keyof IAgentHours];
      const isNextDay24Hours = is24HourDay(nextDaySlots);
      if (
        nextDaySlots &&
        nextDaySlots.length > 0 &&
        nextDaySlots[0].open === "00:00" &&
        !isNextDay24Hours
      ) {
        const slotToAbsorb = nextDaySlots.shift(); // remove slot from the next day
        lastSlotOfTheDay.close = slotToAbsorb?.close || "23:59"; // extend the current day's last slot to the end of the absorbed slot
      }
    }
  }

  return frontendSchedule;
}
